// /opt/isp_erp/frontend/src/components/warehouse/materiales/MovimientosMaterialesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, ButtonGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid, Alert, CircularProgress, TablePagination,
  FormControl, InputLabel, Select, MenuItem, Tooltip
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { warehouseAPI } from '../../../api/warehouse.api';
import {
  Kpi, CampoBusqueda, SelectProveedor, detalleError, fmtNum, fechaHora, fechaLocal, useDebouncedValue, recorte
} from './utils';

const TIPOS_MOV = [
  { v: 'ASIGNACION_TECNICO', l: 'Asignación a técnico', color: 'primary' },
  { v: 'USO_INSTALACION', l: 'Uso en instalación', color: 'error' },
  { v: 'DEVOLUCION_BODEGA', l: 'Devolución a bodega', color: 'warning' },
  { v: 'DEVOLUCION_RETIRO', l: 'Devolución desde retiro', color: 'info' },
  { v: 'ENVIO_SUCURSAL', l: 'Envío a sucursal', color: 'secondary' },
  { v: 'RECEPCION_SUCURSAL', l: 'Recepción en sucursal', color: 'success' },
  { v: 'DIFERENCIA_ENVIO', l: 'Diferencia en el envío', color: 'error' },
  { v: 'ANULACION_ENVIO', l: 'Anulación de envío', color: 'default' },
  { v: 'DEVOLUCION_CENTRAL', l: 'Devolución a la central', color: 'warning' }
];
const infoTipo = (v) => TIPOS_MOV.find((t) => t.v === v) || { l: v, color: 'default' };

const MovimientosMaterialesPanel = ({ tecnicos, proveedores, tecnicoInicial, ctx }) => {
  const hoy = fechaLocal(new Date());
  const [texto, setTexto] = useState('');
  const busqueda = useDebouncedValue(texto.trim(), 400);
  const [tecnicoId, setTecnicoId] = useState(tecnicoInicial || '');
  const [proveedorId, setProveedorId] = useState('');
  const [tipoMov, setTipoMov] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [desde, setDesde] = useState(`${hoy.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage };
      if (busqueda) params.q = busqueda;
      if (tecnicoId !== '') params.tecnico_id = tecnicoId;
      if (proveedorId !== '') params.proveedor_id = proveedorId;
      if (tipoMov) params.tipo_movimiento = tipoMov;
      if (ubicacion && !(ctx && ctx.central === false)) params.ubicacion = ubicacion;
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const r = await warehouseAPI.listarMovimientosMaterialesPaginado(params);
      setData(r.data);
    } catch (err) {
      setData({ items: [], total: 0 });
      setError(detalleError(err, 'Error al cargar los movimientos'));
    } finally {
      setLoading(false);
    }
  }, [busqueda, tecnicoId, proveedorId, tipoMov, ubicacion, ctx, desde, hasta, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const rango = (tipo) => {
    setPage(0);
    if (tipo === 'hoy') {
      setDesde(fechaLocal(new Date()));
      setHasta(fechaLocal(new Date()));
    } else if (tipo === 'mes') {
      const h = fechaLocal(new Date());
      setDesde(`${h.slice(0, 8)}01`);
      setHasta(h);
    } else {
      setDesde('');
      setHasta('');
    }
  };

  const nombreTecnico = (tecnicos || []).find((t) => t.id === tecnicoId);

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Movimientos de materiales</Typography>
          <Typography variant="body2" color="text.secondary">
            Asignaciones, usos en instalaciones y devoluciones, con el stock antes y después.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <Kpi
              titulo="Movimientos"
              valor={data.total}
              detalle={nombreTecnico ? `Técnico: ${nombreTecnico.username || nombreTecnico.email}` : 'Según los filtros aplicados'}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Desde" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Hasta" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => rango('hoy')}>Hoy</Button>
                <Button onClick={() => rango('mes')}>Este mes</Button>
                <Button onClick={() => rango('todo')}>Todo</Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <CampoBusqueda value={texto} onChange={setTexto} label="Buscar material, código u observación" />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Técnico</InputLabel>
                <Select label="Técnico" value={tecnicoId} onChange={(e) => { setTecnicoId(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(tecnicos || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.username || t.nombre || t.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.75}>
              <SelectProveedor value={proveedorId} proveedores={proveedores} onChange={(e) => { setProveedorId(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de movimiento</InputLabel>
                <Select label="Tipo de movimiento" value={tipoMov} onChange={(e) => { setTipoMov(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  {TIPOS_MOV.map((t) => (<MenuItem key={t.v} value={t.v}>{t.l}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            {ctx && ctx.central !== false && (ctx.sucursales || []).length > 0 && (
              <Grid item xs={12} md={2.75}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ubicación</InputLabel>
                  <Select label="Ubicación" value={ubicacion} onChange={(e) => { setUbicacion(e.target.value); setPage(0); }}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="central">Bodega central</MenuItem>
                    {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Fecha y usuario</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Material</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Movimiento</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Origen → Destino</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Cantidad</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Observaciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : data.items.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No hay movimientos con esos filtros</TableCell></TableRow>
              ) : (
                data.items.map((m) => {
                  const t = infoTipo(m.tipo_movimiento);
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography variant="body2">{fechaHora(m.fecha_movimiento)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{m.usuario_nombre || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight="bold">{m.material_nombre}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{m.material_codigo}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={t.color} label={t.l} />
                        {m.ticket_id && (
                          <Typography variant="caption" color="text.secondary" display="block">Ticket ID {m.ticket_id}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">{m.origen}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">→ {m.destino}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight="bold">{fmtNum(m.cantidad)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {fmtNum(m.cantidad_antes)} → {fmtNum(m.cantidad_despues)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={m.observaciones || ''} disableHoverListener={!m.observaciones}>
                          <Typography variant="body2" sx={recorte}>{m.observaciones || '—'}</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </CardContent>
    </Card>
  );
};

export default MovimientosMaterialesPanel;
